"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";

function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <title>Sun</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.95 7.95l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <title>Moon</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z"
      />
    </svg>
  );
}

function LanguageToggle({ size = "sm" }: { size?: "sm" | "md" }) {
  const { language, setLanguage } = useLanguage();

  const pill = size === "sm" ? "px-3 py-1 text-xs" : "px-4 py-1.5 text-sm";
  const activeCls = `${pill} rounded-full bg-brand-primary text-black font-bold transition-all`;
  const inactiveCls = `${pill} rounded-full text-text-secondary hover:text-text-primary transition-all`;

  return (
    <div className="flex bg-border-default rounded-full p-1 border border-border-hover">
      <button
        type="button"
        className={language === "en" ? activeCls : inactiveCls}
        onClick={() => setLanguage("en")}
        aria-pressed={language === "en"}
      >
        EN
      </button>
      <button
        type="button"
        className={language === "es" ? activeCls : inactiveCls}
        onClick={() => setLanguage("es")}
        aria-pressed={language === "es"}
      >
        ES
      </button>
    </div>
  );
}

function ThemeToggle({ size = "sm" }: { size?: "sm" | "md" }) {
  const { theme, toggleTheme, t } = useThemeToggle();

  const iconSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
  const btnSize =
    size === "sm"
      ? "p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-border-hover transition-colors"
      : "p-2.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-border-hover transition-colors";

  return (
    <button
      type="button"
      className={btnSize}
      onClick={toggleTheme}
      aria-label={t}
    >
      {theme === "dark" ? (
        <SunIcon className={iconSize} />
      ) : (
        <MoonIcon className={iconSize} />
      )}
    </button>
  );
}

/** Shared hook so both LanguageToggle and ThemeToggle have access to t() */
function useThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  return {
    theme,
    toggleTheme,
    t: theme === "dark" ? t("theme.switchToLight") : t("theme.switchToDark"),
  };
}

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <header className="relative z-50 w-full max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
      <Link href="/" className="relative z-50">
        {/* Light mode logo (dark text) */}
        <Image
          src="/images/cloudops-logo-light.svg"
          alt="CloudOps Logo"
          width={160}
          height={40}
          className="h-10 w-auto block dark:hidden"
          priority
        />
        {/* Dark mode logo (white text) */}
        <Image
          src="/images/cloudops-logo.svg"
          alt="CloudOps Logo"
          width={160}
          height={40}
          className="h-10 w-auto hidden dark:block"
          priority
        />
      </Link>

      {/* Desktop Nav */}
      <nav className="hidden md:flex items-center gap-6 text-sm text-text-secondary font-medium">
        <Link
          href="/#beneficios"
          className="hover:text-text-primary transition-colors"
        >
          {t("nav.benefits")}
        </Link>
        <Link
          href="/#problemas"
          className="hover:text-text-primary transition-colors"
        >
          {t("nav.problems")}
        </Link>
        <Link
          href="/#workflows"
          className="hover:text-text-primary transition-colors"
        >
          {t("nav.workflows")}
        </Link>
        <Link
          href="/posts/"
          className="hover:text-text-primary transition-colors"
        >
          {t("nav.blog")}
        </Link>
        <Link
          href="/owner/"
          className="hover:text-text-primary transition-colors"
        >
          {t("nav.owner")}
        </Link>

        {/* Desktop Controls */}
        <div className="flex items-center gap-2 ml-2">
          <LanguageToggle size="sm" />
          <ThemeToggle size="sm" />
        </div>
      </nav>

      {/* Mobile Menu Toggle */}
      <button
        type="button"
        className="md:hidden relative z-50 p-2 text-text-secondary hover:text-text-primary"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label={t("nav.toggleMenu")}
      >
        {isMenuOpen ? (
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            role="img"
            aria-labelledby="menu-close-title"
          >
            <title id="menu-close-title">{t("nav.menuClose")}</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            role="img"
            aria-labelledby="menu-open-title"
          >
            <title id="menu-open-title">{t("nav.menuOpen")}</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        )}
      </button>

      {/* Mobile Nav Overlay */}
      <div
        className={`fixed inset-0 bg-surface/95 backdrop-blur-xl z-40 flex items-center justify-center transition-opacity duration-300 md:hidden ${isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        <nav className="flex flex-col gap-8 text-center text-xl font-bold text-text-primary">
          <Link
            href="/#beneficios"
            onClick={() => setIsMenuOpen(false)}
            className="hover:text-brand-secondary transition-colors"
          >
            {t("nav.benefits")}
          </Link>
          <Link
            href="/#problemas"
            onClick={() => setIsMenuOpen(false)}
            className="hover:text-brand-secondary transition-colors"
          >
            {t("nav.problems")}
          </Link>
          <Link
            href="/#workflows"
            onClick={() => setIsMenuOpen(false)}
            className="hover:text-brand-secondary transition-colors"
          >
            {t("nav.workflows")}
          </Link>
          <Link
            href="/posts/"
            onClick={() => setIsMenuOpen(false)}
            className="hover:text-brand-secondary transition-colors"
          >
            {t("nav.blog")}
          </Link>
          <Link
            href="/owner/"
            onClick={() => setIsMenuOpen(false)}
            className="hover:text-brand-secondary transition-colors"
          >
            {t("nav.owner")}
          </Link>

          {/* Mobile Controls */}
          <div className="flex items-center justify-center gap-3 pt-4 border-t border-border-hover">
            <LanguageToggle size="md" />
            <ThemeToggle size="md" />
          </div>
        </nav>
      </div>
    </header>
  );
}
